import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { TRANSITION, motionSafe } from '@/shared/constants/motion';
import { useConsent } from '@/shared/hooks/useConsent';
import { Switch } from '@/shared/components/Switch';

interface ToggleProps {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}

function Toggle({ id, label, hint, checked, disabled = false, onChange }: ToggleProps): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="min-w-0">
        <label id={`${id}-label`} htmlFor={id} className="block text-body font-medium text-[var(--ink)]">{label}</label>
        <p id={`${id}-hint`} className="m-0 text-ui text-[var(--ink-3)]">{hint}</p>
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onChange?.(next)}
        aria-labelledby={`${id}-label`}
        aria-describedby={`${id}-hint`}
        className="shrink-0"
      />
    </div>
  );
}

/**
 * GDPR consent bar for the public pages (blueprint S01 "cookie consent sheet").
 * One sentence, Accept all, Only necessary, and Settings that reveal two
 * toggles. Accept and decline carry equal weight (no nudging), so neither is
 * the page's primary button. Renders nothing once a choice is stored.
 */
export interface ConsentBarProps {
  /**
   * Inside the app shell: sit above the phone tab bar (64px + safe area)
   * below lg. Placed at bottom-0 there, the tab bar covered most of the
   * buttons and taps landed on Inbox/Plan/Compose.
   */
  aboveTabBar?: boolean;
}

export function ConsentBar({ aboveTabBar = false }: ConsentBarProps = {}): JSX.Element | null {
  const { t } = useTranslation();
  const reduced = useReducedMotion() ?? false;
  const { hasChoice, acceptAll, onlyNecessary, save } = useConsent();
  const [open, setOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  return (
    <AnimatePresence>
      {!hasChoice && (
        <motion.aside
          key="consent"
          role="region"
          aria-label={t('consent.title')}
          data-testid="consent-bar"
          initial={reduced ? false : { y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1, transition: motionSafe(TRANSITION.panel, reduced) }}
          exit={{ y: reduced ? 0 : 24, opacity: 0, transition: motionSafe(TRANSITION.micro, reduced) }}
          className={['fixed inset-x-0 z-40 px-4 pb-4', aboveTabBar ? 'bottom-[calc(64px+env(safe-area-inset-bottom,0px))] lg:bottom-0' : 'bottom-0'].join(' ')}
        >
          <div className="mx-auto max-w-[920px] rounded-[16px] border border-[var(--line)] bg-[var(--color-surface)] shadow-[var(--shadow-pop)] p-4 sm:p-5">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
              <p className="m-0 flex-1 text-body leading-[1.5] text-[var(--ink-2)]">{t('consent.sentence')}</p>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls="consent-settings" className="btn btn-ghost min-h-[44px]">
                  {t('consent.settings')}
                </button>
                <button type="button" onClick={onlyNecessary} className="btn btn-secondary min-h-[44px]">
                  {t('consent.onlyNecessary')}
                </button>
                <button type="button" onClick={acceptAll} className="btn btn-secondary min-h-[44px]">
                  {t('consent.acceptAll')}
                </button>
              </div>
            </div>

            {open && (
              <div id="consent-settings" className="mt-3 pt-3 border-t border-[var(--line)]">
                <Toggle id="consent-necessary" label={t('consent.necessary')} hint={t('consent.necessaryHint')} checked disabled />
                <Toggle id="consent-analytics" label={t('consent.analytics')} hint={t('consent.analyticsHint')} checked={analytics} onChange={setAnalytics} />
                <Toggle id="consent-marketing" label={t('consent.marketing')} hint={t('consent.marketingHint')} checked={marketing} onChange={setMarketing} />
                <div className="flex justify-end pt-2">
                  <button type="button" onClick={() => save({ analytics, marketing })} className="btn btn-secondary min-h-[44px]">
                    {t('consent.saveChoice')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
